import { useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useTheme } from 'styled-components';
import { useMixpanel } from '../../contexts';
import { MixpanelEvent } from '../../tokens';
import { format, readableDay, type DateFnInput } from '../../utils';
import { Alert, AlertVariants } from '../Alert';
import { AlertBanner } from '../AlertBanner';
import { Button } from '../Button';
import { Box, HStack } from '../Core';
import { Divider } from '../Divider';
import { FormControlSizes } from '../Form';
import { IconName } from '../Icons';
import { FormattedMessage } from '../Intl';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import type { RestBlotterPaginationLimit } from './types';
import type { UseWsBlotterTable } from './useWsBlotterTable';

const messages = defineMessages({
  tooManyRecords: {
    defaultMessage: 'Too many records ({recordsReceived}), only showing results through {date}',
    id: 'BlotterTable.tooManyRecords',
  },
  tooManyRecordsRest: {
    defaultMessage: 'Display limit reached. Only the first {recordsReceived} entries are shown.',
    id: 'BlotterTable.tooManyRecordsRest',
  },
  loadingAllRecords: {
    defaultMessage: 'Loading all records...',
    id: 'BlotterTable.loadingAllRecords',
  },
  loadAllRecords: {
    defaultMessage: 'Load All',
    id: 'BlotterTable.loadAllRecords',
  },
  loadMoreRecords: {
    defaultMessage: 'Load more records',
    id: 'BlotterTable.loadMoreRecords',
  },
  loadMoreRecordsDescription: {
    defaultMessage:
      'Raise the pagination limit. If done excessively this can cause performance issues with the application.',
    id: 'BlotterTable.loadMoreRecordsDescription',
  },
  dismiss: {
    defaultMessage: 'Dismiss',
    id: 'BlotterTable.dismiss',
  },
});

type WSBlotterTableMaxRecordsReachedWarningProps<
  TOutputType extends object,
  TInputType extends object = TOutputType,
> = UseWsBlotterTable<TOutputType, TInputType>['paginationLimit'] & {
  getTimestamp: (item: TOutputType) => string | undefined;
};

export function WSBlotterTableMaxRecordsReachedWarning<TRowType extends object>({
  limitReached,
  lastRecord,
  recordsReceived,
  raiseLimit,
  getTimestamp,
}: WSBlotterTableMaxRecordsReachedWarningProps<TRowType>) {
  // We hold our own state here to enable dismissing of the limit reached alert.
  const [showLimitReached, setShowLimitReached] = useState(!!limitReached);

  useEffect(() => {
    setShowLimitReached(!!limitReached);
  }, [limitReached]);

  return (
    <MaxRecordsReachedWarning
      showLimitReached={showLimitReached}
      dismiss={() => setShowLimitReached(false)}
      earliestTimestamp={lastRecord ? getTimestamp(lastRecord) : undefined}
      recordsReceived={recordsReceived}
      onRaiseLimitClicked={raiseLimit}
    />
  );
}

export function RestBlotterTableMaxRecordsReachedWarning({
  limitReached,
  recordsReceived,
  loadAll,
  isLoading,
}: RestBlotterPaginationLimit) {
  const mixpanel = useMixpanel();
  // We hold our own state here to enable dismissing of the limit reached alert.
  const [showLimitReached, setShowLimitReached] = useState(!!limitReached);

  useEffect(() => {
    // Only hide the banner if limitReached is false AND we're not loading
    // This prevents the banner from briefly disappearing when loading starts
    if (limitReached) {
      setShowLimitReached(true);
    } else if (!isLoading) {
      setShowLimitReached(false);
    }
  }, [limitReached, isLoading]);

  const handleLoadAllClicked = () => {
    mixpanel.track(MixpanelEvent.RaisePaginationLimit);
    loadAll();
  };

  const formattedRecordsReceived = format(recordsReceived);

  // Show banner during loading OR when limit is reached (and user hasn't dismissed)
  if (!showLimitReached && !isLoading) {
    return null;
  }

  return (
    <>
      <AlertBanner variant={AlertVariants.Notice}>
        {isLoading ? (
          <FormattedMessage {...messages.loadingAllRecords} />
        ) : (
          <FormattedMessage {...messages.tooManyRecordsRest} values={{ recordsReceived: formattedRecordsReceived }} />
        )}
        {!isLoading && (
          <>
            <Divider orientation="vertical" ml="spacing4" mr="-spacing4" />
            <Box my="-spacing4">
              <Button
                ghost
                onClick={handleLoadAllClicked}
                size={FormControlSizes.Small}
                endIcon={IconName.Refresh}
                data-testid="rest-blotter-warning-load-all-button"
              >
                <FormattedMessage {...messages.loadAllRecords} />
              </Button>
            </Box>
            <Divider orientation="vertical" ml="spacing4" mr="-spacing4" />
            <Box my="-spacing4">
              <Button
                ghost
                endIcon={IconName.Close}
                size={FormControlSizes.Small}
                onClick={() => setShowLimitReached(false)}
                data-testid="rest-blotter-warning-dismiss-button"
              >
                <FormattedMessage {...messages.dismiss} />
              </Button>
            </Box>
          </>
        )}
      </AlertBanner>
      <Divider mx="-spacing24" color="backgroundDivider" />
    </>
  );
}

interface MaxRecordsReachedWarningProps {
  showLimitReached: boolean;
  recordsReceived?: number;
  dismiss?: () => void;
  earliestTimestamp?: DateFnInput;
  onRaiseLimitClicked?: () => void;
}

export function MaxRecordsReachedWarning({
  showLimitReached,
  dismiss,
  recordsReceived,
  earliestTimestamp,
  onRaiseLimitClicked,
}: MaxRecordsReachedWarningProps) {
  const theme = useTheme();
  const mixpanel = useMixpanel();

  const handleRaiseLimitClicked = () => {
    mixpanel.track(MixpanelEvent.RaisePaginationLimit);
    onRaiseLimitClicked?.();
  };

  const showRaiseLimitButton = onRaiseLimitClicked != null;
  const formattedRecordsReceived = format(recordsReceived);

  return (
    <>
      {showLimitReached && (
        <Alert
          variant={AlertVariants.Warning}
          dismissable={!!dismiss}
          onDismiss={dismiss}
          mx="spacing12"
          my="spacing4"
          alignItems="center"
        >
          <HStack w="100%" justifyContent="flex-start" gap="spacing12">
            <Text size={theme.fontSizeDefault} color={theme.colors.gray['100']}>
              <FormattedMessage
                {...messages.tooManyRecords}
                values={{ recordsReceived: formattedRecordsReceived, date: readableDay(earliestTimestamp, true) }}
              />
            </Text>
            {showRaiseLimitButton && (
              <Tooltip tooltip={<FormattedMessage {...messages.loadMoreRecordsDescription} />}>
                <Button onClick={handleRaiseLimitClicked} size={FormControlSizes.Small}>
                  <FormattedMessage {...messages.loadMoreRecords} />
                </Button>
              </Tooltip>
            )}
          </HStack>
        </Alert>
      )}
    </>
  );
}
